<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import ProjectList from '../components/ProjectList.vue'
import { API_BASE_URL } from '../config'

const props = defineProps<{
  activeTab: string
}>()

const activeCategory = ref('all')
const showTagModal = ref(false)
const newTagName = ref('')

// 添加项目抽屉
const showAddDrawer = ref(false)
const newProject = ref({
  title: '',
  cat_id: 0,
  description: '',
  startup_type: '0',
  market_score: 5,
  product_score: 5,
  tech_score: 5,
  business_score: 5
})
const addingProject = ref(false)

// 评分悬停状态
const hoverMarketScore = ref(0)
const hoverProductScore = ref(0)
const hoverTechScore = ref(0)
const hoverBusinessScore = ref(0)

// 项目分类选项
const projectCategories = ref<{ id: number; name: string }[]>([])

// 打开添加抽屉
const openAddDrawer = () => {
  showAddDrawer.value = true
  newProject.value = {
    title: '',
    cat_id: 0,
    description: '',
    startup_type: '0',
    market_score: 5,
    product_score: 5,
    tech_score: 5,
    business_score: 5
  }
  // 确保标签列表已加载
  if (projectCategories.value.length === 0) {
    fetchTags()
  }
}

// 关闭添加抽屉
const closeAddDrawer = () => {
  showAddDrawer.value = false
}

// 暴露方法给父组件
defineExpose({
  openAddDrawer
})

// 提交添加项目
const submitAddProject = async () => {
  if (!newProject.value.title.trim()) {
    alert('请输入项目名称')
    return
  }
  if (newProject.value.cat_id === 0) {
    alert('请选择项目分类')
    return
  }
  
  addingProject.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/api/startup/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tag_id: newProject.value.cat_id,
        title: newProject.value.title,
        description: newProject.value.description,
        market_score: newProject.value.market_score,
        product_score: newProject.value.product_score,
        tech_score: newProject.value.tech_score,
        business_score: newProject.value.business_score
      })
    })
    const result = await response.json()
    if (result.code === 1) {
      closeAddDrawer()
      // 刷新项目列表
      fetchProjects()
    } else {
      alert(result.msg || '添加失败')
    }
  } catch (error) {
    console.error('添加项目失败:', error)
    alert('添加失败，请稍后重试')
  } finally {
    addingProject.value = false
  }
}

// 标签管理
const openTagModal = () => {
  showTagModal.value = true
  fetchTags()
}

const closeTagModal = () => {
  showTagModal.value = false
  newTagName.value = ''
}

// 获取标签列表
const fetchingTags = ref(false)
const fetchTags = async () => {
  fetchingTags.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/api/startup/tag`)
    const result = await response.json()
    if (result.code === 1) {
      // 保留"全部"，后面接接口返回的标签
      const apiTags = result.data.map((tag: any) => ({
        key: String(tag.id),
        label: tag.name,
        id: tag.id,
        sort: tag.sort,
        status: tag.status
      }))
      categories.value = [{ key: 'all', label: '全部' }, ...apiTags]
      // 同步更新项目分类选项
      projectCategories.value = apiTags.map((tag: any) => ({
        id: tag.id,
        name: tag.label || tag.name
      }))
    }
  } catch (error) {
    console.error('获取标签失败:', error)
  } finally {
    fetchingTags.value = false
  }
}

// 添加标签
const addingTag = ref(false)
const addTag = async () => {
  if (!newTagName.value.trim()) return
  
  addingTag.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/api/startup/tag/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: newTagName.value.trim(),
        sort: categories.value.length - 1
      })
    })
    const result = await response.json()
    if (result.code === 1) {
      // 重新获取标签列表
      await fetchTags()
      newTagName.value = ''
    } else {
      alert(result.msg || '添加失败')
    }
  } catch (error) {
    console.error('添加标签失败:', error)
    alert('添加失败，请稍后重试')
  } finally {
    addingTag.value = false
  }
}

// 删除标签确认弹窗
const showDeleteTagConfirm = ref(false)
const tagToDelete = ref<Tag | null>(null)
const deletingTagId = ref<number | null>(null)

// 打开删除确认弹窗
const confirmDeleteTag = (index: number) => {
  if (index === 0) return // "全部"不能删除
  const tag = categories.value[index]
  if (!tag.id) return
  tagToDelete.value = tag
  showDeleteTagConfirm.value = true
}

// 关闭删除确认弹窗
const cancelDeleteTag = () => {
  showDeleteTagConfirm.value = false
  tagToDelete.value = null
}

// 执行删除标签
const removeTag = async () => {
  if (!tagToDelete.value?.id) return
  
  deletingTagId.value = tagToDelete.value.id
  try {
    const response = await fetch(`${API_BASE_URL}/api/startup/tag/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: tagToDelete.value.id })
    })
    const result = await response.json()
    if (result.code === 1) {
      // 如果当前选中的是被删除的标签，切换到"全部"
      if (activeCategory.value === tagToDelete.value.key) {
        activeCategory.value = 'all'
      }
      // 关闭弹窗并重新获取标签列表
      cancelDeleteTag()
      await fetchTags()
    } else {
      alert(result.msg || '删除失败')
    }
  } catch (error) {
    console.error('删除标签失败:', error)
    alert('删除失败，请稍后重试')
  } finally {
    deletingTagId.value = null
  }
}

// 标签类型
interface Tag {
  key: string
  label: string
  id?: number
  sort?: number
  status?: number
}

const categories = ref<Tag[]>([
  { key: 'all', label: '全部' }
])

// 项目类型
interface Project {
  id: number
  tag_id: number
  title: string
  description: string
  sort: number
  create_at: number
  thumb: string
  startup_type: string
  market_score: number
  product_score: number
  tech_score: number
  business_score: number
}

// 项目列表数据
const allProjects = ref<Project[]>([])
const loadingProjects = ref(false)
const currentPage = ref(1)
const pageSize = ref(10)
const totalProjects = ref(0)

// 获取项目列表
const fetchProjects = async () => {
  loadingProjects.value = true
  try {
    const params = new URLSearchParams()
    params.append('page', String(currentPage.value))
    params.append('pageSize', String(pageSize.value))
    
    // 标签筛选
    if (activeCategory.value !== 'all') {
      const tag = categories.value.find(c => c.key === activeCategory.value)
      if (tag?.id) {
        params.append('tag_id', String(tag.id))
      }
    }
    
    // 类型筛选
    if (props.activeTab) {
      params.append('startup_type', props.activeTab)
    }
    
    const response = await fetch(`${API_BASE_URL}/api/startup?${params.toString()}`)
    const result = await response.json()
    if (result.code === 1) {
      allProjects.value = result.data.list || []
      totalProjects.value = result.data.total || 0
    }
  } catch (error) {
    console.error('获取项目列表失败:', error)
  } finally {
    loadingProjects.value = false
  }
}

const filteredProjects = computed(() => allProjects.value)

// 页面加载时获取数据
onMounted(() => {
  fetchTags().then(() => {
    fetchProjects()
  })
})

// 监听筛选条件变化，重新获取项目列表
watch([() => props.activeTab, activeCategory], () => {
  currentPage.value = 1
  fetchProjects()
})
</script>

<template>
  <div class="home-page">
    <div class="category-bar">
      <div class="category-list">
        <button
          v-for="cat in categories"
          :key="cat.key"
          class="category-btn"
          :class="{ active: activeCategory === cat.key }"
          @click="activeCategory = cat.key"
        >
          {{ cat.label }}
        </button>
      </div>
      <button class="manage-tags-btn" @click="openTagModal" title="管理标签">
        ✏️
      </button>
    </div>
        <!-- 加载状态 -->
        <div v-if="loadingProjects" class="page-loading">
          <span class="loading-spinner"></span>
          <span>加载中...</span>
        </div>
        <!-- 空状态 -->
        <div v-else-if="filteredProjects.length === 0" class="empty-state">
          <div class="empty-icon">📝</div>
          <div class="empty-text">你还没添加项目哦~</div>
        </div>
        <ProjectList v-else :projects="filteredProjects" />

    <!-- 添加项目抽屉 -->
    <div v-if="showAddDrawer" class="drawer-overlay" @click.self="closeAddDrawer">
      <div class="drawer-content">
        <div class="drawer-header">
          <h3>添加项目</h3>
          <button class="close-btn" @click="closeAddDrawer">×</button>
        </div>
        <div class="drawer-body">
          <div class="form-group">
            <label>项目名称</label>
            <input v-model="newProject.title" type="text" placeholder="请输入项目名称" />
          </div>
          <div class="form-group">
            <label>项目分类</label>
            <select v-model="newProject.cat_id">
              <option :value="0">请选择分类</option>
              <option v-for="cat in projectCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>项目描述</label>
            <textarea v-model="newProject.description" rows="3" placeholder="请输入项目描述"></textarea>
          </div>

          <!-- 评分区域 -->
          <div class="form-group ratings-row">
            <label>可行性评分</label>
            <div class="ratings-container">
              <!-- 市场可行性评分 -->
              <div class="rating-item" @mouseleave="hoverMarketScore = 0">
                <span class="rating-label">市场</span>
                <div class="star-rating small">
                  <span
                    v-for="n in 10"
                    :key="n"
                    class="star"
                    :class="{ active: n <= (hoverMarketScore || newProject.market_score) }"
                    @mouseenter="hoverMarketScore = n"
                    @click="newProject.market_score = n"
                  >★</span>
                </div>
              </div>

              <!-- 产品可行性评分 -->
              <div class="rating-item" @mouseleave="hoverProductScore = 0">
                <span class="rating-label">产品</span>
                <div class="star-rating small">
                  <span
                    v-for="n in 10"
                    :key="n"
                    class="star"
                    :class="{ active: n <= (hoverProductScore || newProject.product_score) }"
                    @mouseenter="hoverProductScore = n"
                    @click="newProject.product_score = n"
                  >★</span>
                </div>
              </div>

              <!-- 技术可行性评分 -->
              <div class="rating-item" @mouseleave="hoverTechScore = 0">
                <span class="rating-label">技术</span>
                <div class="star-rating small">
                  <span
                    v-for="n in 10"
                    :key="n"
                    class="star"
                    :class="{ active: n <= (hoverTechScore || newProject.tech_score) }"
                    @mouseenter="hoverTechScore = n"
                    @click="newProject.tech_score = n"
                  >★</span>
                </div>
              </div>

              <!-- 商业模式评分 -->
              <div class="rating-item" @mouseleave="hoverBusinessScore = 0">
                <span class="rating-label">商业</span>
                <div class="star-rating small">
                  <span
                    v-for="n in 10"
                    :key="n"
                    class="star"
                    :class="{ active: n <= (hoverBusinessScore || newProject.business_score) }"
                    @mouseenter="hoverBusinessScore = n"
                    @click="newProject.business_score = n"
                  >★</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="drawer-footer">
          <button class="btn ghost" @click="closeAddDrawer">取消</button>
          <button class="btn primary" :disabled="addingProject" @click="submitAddProject">
            <span v-if="addingProject" class="btn-loading"></span>
            <span v-else>添加</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 标签管理弹窗 -->
    <div v-if="showTagModal" class="modal-overlay" @click.self="closeTagModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>管理标签</h3>
          <button class="close-btn" @click="closeTagModal">×</button>
        </div>
        <div class="modal-body">
          <div v-if="fetchingTags" class="loading-state">
            <span class="loading-spinner"></span>
            <span>加载中...</span>
          </div>
          <template v-else>
            <div class="tag-list">
              <div
                v-for="(cat, index) in categories"
                :key="cat.key"
                class="tag-item"
              >
                <span class="tag-name">{{ cat.label }}</span>
                <button
                  v-if="index > 0"
                  class="remove-btn"
                  :disabled="deletingTagId === cat.id"
                  @click="confirmDeleteTag(index)"
                  title="删除"
                >
                  <span v-if="deletingTagId === cat.id" class="btn-loading-small"></span>
                  <span v-else>×</span>
                </button>
              </div>
            </div>
            <div class="add-tag-form">
              <input
                v-model="newTagName"
                type="text"
                placeholder="输入新标签名称"
                :disabled="addingTag"
                @keyup.enter="addTag"
              />
              <button class="add-tag-btn" :disabled="addingTag" @click="addTag">
                <span v-if="addingTag" class="btn-loading-small"></span>
                <span v-else>添加</span>
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- 删除标签确认弹窗 -->
    <div v-if="showDeleteTagConfirm" class="modal-overlay" @click.self="cancelDeleteTag">
      <div class="modal-content delete-modal-content">
        <div class="modal-header">
          <h3>删除标签</h3>
          <button class="close-btn" @click="cancelDeleteTag">×</button>
        </div>
        <div class="modal-body">
          <p class="confirm-text">
            确定要删除标签"{{ tagToDelete?.label }}"吗？<br>
            <span class="warning-text">该标签下的项目将变为未分类状态</span>
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn ghost" @click="cancelDeleteTag">取消</button>
          <button class="btn danger" :disabled="deletingTagId !== null" @click="removeTag">
            <span v-if="deletingTagId !== null" class="btn-loading"></span>
            <span v-else>删除</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.home-page {
  height: 100%;
}

.category-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
}

.category-list {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow-x: auto;
  flex: 1;
}

.manage-tags-btn {
  padding: 0.35rem;
  border: none;
  background: transparent;
  color: #bbb;
  font-size: 0.9rem;
  cursor: pointer;
  white-space: nowrap;
  margin-left: 0.5rem;
  transition: all 0.2s;
  line-height: 1;
}

.manage-tags-btn:hover {
  color: #42b883;
}

.category-btn {
  padding: 0.4rem 1rem;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #666;
  font-size: 0.85rem;
  cursor: pointer;
  border-radius: 16px;
  transition: all 0.2s;
  white-space: nowrap;
}

.category-btn:hover {
  border-color: #42b883;
  color: #42b883;
}

.category-btn.active {
  background: #42b883;
  border-color: #42b883;
  color: #fff;
}

.page-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6rem;
  gap: 1.5rem;
  color: #999;
}

.page-loading .loading-spinner {
  width: 40px;
  height: 40px;
  border-width: 3px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6rem 2rem;
  gap: 1rem;
}

.empty-icon {
  font-size: 4rem;
  opacity: 0.5;
}

.empty-text {
  font-size: 0.9rem;
  color: #999;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #999;
  cursor: pointer;
  line-height: 1;
}

.close-btn:hover {
  color: #666;
}

.modal-body {
  padding: 1.5rem;
  padding-bottom: 0;
}

.tag-list {
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 0.5rem;
}

.tag-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.tag-item:last-child {
  border-bottom: none;
}

.tag-name {
  font-size: 0.9rem;
  color: #333;
}

.remove-btn {
  background: #ffebee;
  border: none;
  color: #f44336;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn:hover {
  background: #f44336;
  color: #fff;
}

.add-tag-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem 0 2rem 0;
  margin-top: 1rem;
  border-top: 1px solid #e8e8e8;
}

.add-tag-form input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.95rem;
  box-sizing: border-box;
  transition: all 0.2s;
}

.add-tag-form input:focus {
  outline: none;
  border-color: #42b883;
  box-shadow: 0 0 0 3px rgba(66, 184, 131, 0.1);
}

.add-tag-form input::placeholder {
  color: #aaa;
}

.add-tag-btn {
  width: 100%;
  padding: 0.75rem 1rem;
  background: #42b883;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(66, 184, 131, 0.3);
}

.add-tag-btn:hover:not(:disabled) {
  background: #369970;
  box-shadow: 0 4px 12px rgba(66, 184, 131, 0.4);
  transform: translateY(-1px);
}

.add-tag-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem;
  color: #999;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #e0e0e0;
  border-top-color: #42b883;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.btn-loading-small {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.remove-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 标签管理弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.modal-content {
  background: #fff;
  border-radius: 12px;
  max-width: 480px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e8e8e8;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.modal-body {
  padding: 1.5rem;
}

.confirm-text {
  font-size: 0.95rem;
  color: #333;
  line-height: 1.6;
}

.warning-text {
  color: #f44336;
  font-size: 0.85rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e8e8e8;
}

.btn.danger {
  background: #f44336;
  color: #fff;
}

.btn.danger:hover:not(:disabled) {
  background: #d32f2f;
}

/* 抽屉样式 */
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.drawer-content {
  position: fixed;
  top: 0;
  right: 0;
  width: 480px;
  height: 100vh;
  background: #fff;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e8e8e8;
}

.drawer-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.drawer-body {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #666;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #42b883;
}

.form-group.scores .score-items {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.score-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.score-item span {
  font-size: 0.85rem;
  color: #666;
}

.score-item input {
  width: 60px;
  text-align: center;
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e8e8e8;
}

.btn {
  padding: 0.5rem 1.25rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn.ghost {
  background: #f2f2f2;
  color: #666;
}

.btn.ghost:hover {
  background: #e0e0e0;
}

.btn.primary {
  background: #42b883;
  color: #fff;
}

.btn.primary:hover:not(:disabled) {
  background: #369970;
}

.btn-loading {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 星星评分样式 */
.ratings-row {
  margin-bottom: 1.5rem;
}

.ratings-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.rating-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.rating-label {
  font-size: 0.85rem;
  color: #666;
}

.star-rating {
  display: flex;
  gap: 0.25rem;
  cursor: pointer;
  user-select: none;
}

.star-rating.small {
  gap: 0.15rem;
}

.star {
  font-size: 1.2rem;
  color: #ddd;
  transition: color 0.2s;
}

.star-rating.small .star {
  font-size: 0.9rem;
}

.star.active {
  color: #ffc107;
}

.star:hover {
  color: #ffc107;
}
</style>
